import React, { useEffect, useState } from "react";
import useInventoryApi from "./useInventoryApi";
import WidgetPanel from "../../ui/WidgetPanel";
import WidgetGrid from "../../ui/WidgetGrid";
import AnalyticsMetricCard from "../../Analytics/AnalyticsMetricCard";
import { num, sumKnown } from "../../Analytics/metricValue";
import "../OpsShared.css";
import "../../../styles/data-table.css";

const count = (v) => {
  const n = num(v);
  return n === null ? "—" : n.toLocaleString();
};

export default function OverviewPage() {
  const { lowStock, consumption, reorderSuggestions, quickReorder } = useInventoryApi();
  const [alerts, setAlerts] = useState([]);
  const [suggest, setSuggest] = useState([]);
  const [month, setMonth] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [a, s, c] = await Promise.all([
          lowStock().catch(() => []),
          reorderSuggestions().catch(() => []),
          consumption({ range: "last_30d" }).catch(() => []),
        ]);
        setAlerts(Array.isArray(a) ? a : (a.items || a || []));
        setSuggest(Array.isArray(s) ? s : (s.items || s || []));
        setMonth(Array.isArray(c) ? c : (c.items || c || []));
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    })();
  }, []); // load once

  // Items used (30d): sum the known rows; an absent total is skipped, not zeroed.
  const used30d = sumKnown(month, (x) => x.total_used).total;

  const kpis = [
    { key: "alerts", title: "Low-stock items", subtitle: "At or below reorder point", value: count(alerts.length), tone: alerts.length ? "bad" : "neutral" },
    { key: "suggest", title: "Suggested POs", subtitle: "Reorders to review", value: count(suggest.length) },
    { key: "usage", title: "Items used (30d)", subtitle: "Consumption, last 30 days", value: count(used30d) },
  ];

  const doQuickReorder = async (row) => {
    try {
      const pid = row.product_id ?? row.id;
      setBusyId(pid);
      const qty = Number(row.suggested_qty ?? row.reorder_target ?? 0);
      const vendor_id = row.vendor_id ?? null;
      if (!qty) {
        alert("No suggested quantity for this item.");
        return;
      }
      await quickReorder({ product_id: pid, qty, vendor_id });
      alert("Draft PO created.");
    } catch (e) {
      console.error(e);
      alert("Could not create draft PO.");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="ops-page">
      <header className="ops-head">
        <div>
          <h1>Inventory overview</h1>
          <div className="ops-muted">What's running low and what to reorder, from the stock ledger.</div>
        </div>
      </header>

      <WidgetGrid columns={3}>
        {kpis.map((k) => (
          <AnalyticsMetricCard
            key={k.key}
            title={k.title}
            subtitle={k.subtitle}
            value={k.value}
            periodLabel="current"
            loading={loading}
            tone={k.tone || "neutral"}
          />
        ))}
      </WidgetGrid>

      <section className="ops-section">
        <div className="ops-section__head"><h3>Low-stock alerts</h3></div>
        <WidgetPanel className="gt-table-shell" padded={false}>
          <div className="gt-table-scroll">
            <div className="gt-table-wrapper">
              <table className="gt-table">
                <thead>
                  <tr><th>SKU</th><th>Name</th><th className="num">On hand</th><th className="num">Min</th><th className="num">Target</th><th aria-label="Actions" /></tr>
                </thead>
                <tbody>
                  {alerts.length === 0 ? (
                    <tr className="gt-row gt-row--static"><td className="gt-cell" colSpan={6}><div className="gt-empty">All good — nothing at or below reorder point.</div></td></tr>
                  ) : alerts.map((a) => {
                    const key = a.product_id ?? a.id ?? a.sku;
                    const pid = a.product_id ?? a.id;
                    return (
                      <tr key={key} className="gt-row gt-row--static">
                        <td className="gt-cell mono">{a.sku || "—"}</td>
                        <td className="gt-cell name-col">{a.name || "—"}</td>
                        <td className="gt-cell num">{count(a.on_hand)}</td>
                        <td className="gt-cell num">{count(a.reorder_min)}</td>
                        <td className="gt-cell num">{count(a.reorder_target)}</td>
                        <td className="gt-cell">
                          <div className="gt-actions">
                            <button type="button" className="gt-link" onClick={() => doQuickReorder(a)} disabled={busyId === pid}>
                              {busyId === pid ? "Adding…" : "Quick reorder"}
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </WidgetPanel>
      </section>

      <section className="ops-section">
        <div className="ops-section__head"><h3>Suggested reorders</h3></div>
        <WidgetPanel className="gt-table-shell" padded={false}>
          <div className="gt-table-scroll">
            <div className="gt-table-wrapper">
              <table className="gt-table">
                <thead>
                  <tr><th>SKU</th><th>Name</th><th className="num">Suggested qty</th><th>Vendor</th><th>Reason</th><th aria-label="Actions" /></tr>
                </thead>
                <tbody>
                  {suggest.length === 0 ? (
                    <tr className="gt-row gt-row--static"><td className="gt-cell" colSpan={6}><div className="gt-empty">No suggestions.</div></td></tr>
                  ) : suggest.map((r) => {
                    const key = r.product_id ?? r.id ?? r.sku;
                    const pid = r.product_id ?? r.id;
                    return (
                      <tr key={key} className="gt-row gt-row--static">
                        <td className="gt-cell mono">{r.sku || "—"}</td>
                        <td className="gt-cell name-col">{r.name || "—"}</td>
                        <td className="gt-cell num">{count(r.suggested_qty)}</td>
                        <td className="gt-cell">{r.vendor_name || "—"}</td>
                        <td className="gt-cell gt-cell--muted">{r.reason || "—"}</td>
                        <td className="gt-cell">
                          <div className="gt-actions">
                            <button type="button" className="gt-link" onClick={() => doQuickReorder(r)} disabled={busyId === pid}>
                              {busyId === pid ? "Adding…" : "Quick reorder"}
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </WidgetPanel>
      </section>
    </div>
  );
}
